'use client';

import { useState, useEffect } from 'react';
import HeroSection from './HeroSection';
import TrendingStories from './TrendingStories';
import CategoryShowcase from './CategoryShowcase';
import CreatorSpotlight from './CreatorSpotlight';
import RegionalHighlights from './RegionalHighlights';
import SocialProof from './SocialProof';
import Footer from './Footer';

interface Story {
  id: number;
  title: string;
  category: string;
  language: string;
  duration: string;
  rating: number;
  listeners: string;
  image: string;
  alt: string;
  trending: boolean;
}

interface Category {
  id: number;
  name: string;
  icon: string;
  count: string;
  color: string;
}

interface Creator {
  id: number;
  name: string;
  role: string;
  bio: string;
  followers: string;
  stories: string;
  rating: number;
  image: string;
  alt: string;
  verified: boolean;
}

interface Region {
  id: number;
  name: string;
  language: string;
  stories: string;
  listeners: string;
  image: string;
  alt: string;
  color: string;
}

interface Testimonial {
  id: number;
  name: string;
  location: string;
  rating: number;
  review: string;
  image: string;
  alt: string;
  date: string;
}

export default function HomepageInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const trendingStories: Story[] = [
  {
    id: 1,
    title: "Dil Ki Baatein",
    category: "Romance",
    language: "Hindi",
    duration: "45 min",
    rating: 4.8,
    listeners: "2.5L",
    image: "https://images.unsplash.com/photo-1704259127485-bbdbf715d0cd",
    alt: "Young Indian couple holding hands walking through colorful flower garden at sunset",
    trending: true
  },
  {
    id: 2,
    title: "Rahasya Ki Raat",
    category: "Thriller",
    language: "Hindi",
    duration: "38 min",
    rating: 4.7,
    listeners: "1.8L",
    image: "https://images.unsplash.com/photo-1641474154472-ae9670367edf",
    alt: "Dark mysterious alleyway with dim streetlights and shadows creating suspenseful atmosphere",
    trending: true
  },
  {
    id: 3,
    title: "காதல் கதை",
    category: "Romance",
    language: "Tamil",
    duration: "52 min",
    rating: 4.9,
    listeners: "3.2L",
    image: "https://images.unsplash.com/photo-1711890714962-65c6123be5e7",
    alt: "Traditional South Indian temple with couple in traditional attire during golden hour",
    trending: true
  },
  {
    id: 4,
    title: "Hasya Kahani",
    category: "Comedy",
    language: "Hindi",
    duration: "30 min",
    rating: 4.6,
    listeners: "1.5L",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11e22bcc2-1766602178154.png",
    alt: "Group of young Indian friends laughing together in modern cafe setting",
    trending: false
  },
  {
    id: 5,
    title: "প্রেমের গল্প",
    category: "Romance",
    language: "Bengali",
    duration: "48 min",
    rating: 4.8,
    listeners: "2.1L",
    image: "https://images.unsplash.com/photo-1705049316680-7befdb819edb",
    alt: "Bengali couple in traditional attire standing by riverside during monsoon season",
    trending: false
  },
  {
    id: 6,
    title: "ప్రేమ కథ",
    category: "Romance",
    language: "Telugu",
    duration: "55 min",
    rating: 4.7,
    listeners: "2.8L",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1739205ae-1767862917027.png",
    alt: "Telugu couple in traditional silk saree and dhoti at temple festival celebration",
    trending: false
  },
  {
    id: 7,
    title: "Bhoot Ki Kahani",
    category: "Horror",
    language: "Hindi",
    duration: "42 min",
    rating: 4.5,
    listeners: "1.9L",
    image: "https://images.unsplash.com/photo-1492019182957-d877e5c37882",
    alt: "Abandoned old Indian haveli mansion with broken windows at night with moonlight",
    trending: false
  },
  {
    id: 8,
    title: "Safar Ki Dastan",
    category: "Adventure",
    language: "Urdu",
    duration: "60 min",
    rating: 4.9,
    listeners: "3.5L",
    image: "https://images.unsplash.com/photo-1598708767374-cbf8886534a7",
    alt: "Himalayan mountain range with trekker silhouette against dramatic sunset sky",
    trending: true
  }];


  const categories: Category[] = [
  { id: 1, name: "Romance", icon: "HeartIcon", count: "12,500+", color: "bg-pink-500" },
  { id: 2, name: "Thriller", icon: "BoltIcon", count: "8,200+", color: "bg-purple-500" },
  { id: 3, name: "Comedy", icon: "FaceSmileIcon", count: "6,800+", color: "bg-yellow-500" },
  { id: 4, name: "Horror", icon: "FireIcon", count: "5,400+", color: "bg-red-500" },
  { id: 5, name: "Drama", icon: "FilmIcon", count: "9,600+", color: "bg-blue-500" },
  { id: 6, name: "Adventure", icon: "GlobeAltIcon", count: "4,200+", color: "bg-green-500" },
  { id: 7, name: "Mystery", icon: "MagnifyingGlassIcon", count: "3,800+", color: "bg-indigo-500" },
  { id: 8, name: "Fantasy", icon: "SparklesIcon", count: "2,900+", color: "bg-violet-500" }];


  const creators: Creator[] = [
  {
    id: 1,
    name: "Priya Sharma",
    role: "Romance Storyteller",
    bio: "Bringing love stories to life with emotion and authenticity. Specializing in contemporary Indian romance narratives.",
    followers: "2.5L",
    stories: "145",
    rating: 4.9,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bca604bb-1763300779044.png",
    alt: "Professional Indian woman in her 30s with warm smile wearing traditional kurta in studio setting",
    verified: true
  },
  {
    id: 2,
    name: "Rajesh Kumar",
    role: "Thriller Narrator",
    bio: "Master of suspense and mystery. Creating spine-chilling audio experiences that keep listeners on edge.",
    followers: "1.8L",
    stories: "98",
    rating: 4.8,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18be388c1-1767120294916.png",
    alt: "Indian male voice artist in his 40s with intense expression in professional recording studio",
    verified: true
  },
  {
    id: 3,
    name: "Ananya Iyer",
    role: "Regional Content Creator",
    bio: "Celebrating South Indian culture through authentic Tamil and Malayalam storytelling traditions.",
    followers: "3.2L",
    stories: "210",
    rating: 4.9,
    image: "https://images.unsplash.com/photo-1710969490647-8ea91d57a6a3",
    alt: "South Indian woman in traditional silk saree with jasmine flowers smiling warmly at camera",
    verified: true
  }];


  const regions: Region[] = [
  {
    id: 1,
    name: "Maharashtra",
    language: "Marathi",
    stories: "8,500+",
    listeners: "1.2L",
    image: "https://images.unsplash.com/photo-1678289296038-5de6f339b33c",
    alt: "Mumbai Gateway of India monument with traditional Marathi cultural elements and sunset backdrop",
    color: "bg-orange-500"
  },
  {
    id: 2,
    name: "Tamil Nadu",
    language: "Tamil",
    stories: "12,200+",
    listeners: "2.5L",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7181479-1765550050994.png",
    alt: "Meenakshi Temple Madurai with intricate gopuram architecture and traditional Tamil cultural motifs",
    color: "bg-red-500"
  },
  {
    id: 3,
    name: "West Bengal",
    language: "Bengali",
    stories: "9,800+",
    listeners: "1.8L",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14320ec39-1765005337790.png",
    alt: "Howrah Bridge Kolkata with traditional Bengali cultural elements during evening illumination",
    color: "bg-yellow-500"
  },
  {
    id: 4,
    name: "Karnataka",
    language: "Kannada",
    stories: "7,600+",
    listeners: "1.5L",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_164b033b0-1765605034789.png",
    alt: "Mysore Palace illuminated at night with traditional Kannada cultural decorations and architecture",
    color: "bg-purple-500"
  }];


  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Sneha Patel",
    location: "Mumbai, Maharashtra",
    rating: 5,
    review: "StoryNest-FM has become my daily companion during commutes. The quality of Hindi stories is exceptional, and I love discovering new regional content every week!",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16bf74f91-1763296713817.png",
    alt: "Young Indian professional woman in business casual attire smiling confidently at camera",
    date: "2 days ago"
  },
  {
    id: 2,
    name: "Arjun Reddy",
    location: "Hyderabad, Telangana",
    rating: 5,
    review: "Finally, an app that celebrates Telugu storytelling! The narrators are talented, and the variety of genres keeps me engaged. Highly recommend to all audio story lovers.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b48d0a1c-1763296743407.png",
    alt: "Indian male professional in his 30s with friendly smile wearing casual shirt outdoors",
    date: "5 days ago"
  },
  {
    id: 3,
    name: "Meera Krishnan",
    location: "Chennai, Tamil Nadu",
    rating: 5,
    review: "The Tamil content library is incredible! From classic tales to modern stories, everything is beautifully narrated. This app has rekindled my love for storytelling.",
    image: "https://images.unsplash.com/photo-1612684352323-b79cfec32b7a",
    alt: "South Indian woman in traditional attire with warm smile in natural outdoor setting",
    date: "1 week ago"
  }];


  const handlePlayAudio = () => {
    if (!isHydrated) return;
    setIsAudioPlaying(!isAudioPlaying);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-96 bg-muted rounded-3xl" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) =>
              <div key={i} className="h-64 bg-muted rounded-2xl" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <HeroSection onPlayAudio={handlePlayAudio} />
      <TrendingStories stories={trendingStories} />
      <CategoryShowcase categories={categories} />
      <CreatorSpotlight creators={creators} />
      <RegionalHighlights regions={regions} />
      <SocialProof testimonials={testimonials} />
      <Footer />
    </div>);

}